import { EventOrigin } from "@/interfaces/EventOrigin";
import { Event } from "@/interfaces/Event";
import { Incident } from "@/interfaces/Incident";
import { Sensor } from "@/interfaces/Sensor";
import { Data } from "@/interfaces/Data";
import { Burst, SingleBurst } from "@/interfaces/Burst";
import { ConditionMatchedPeriod } from "@/interfaces/ConditionMatchedPeriod";
import { Aggregator } from "@/interfaces/Aggregator";
import { Task } from "@/interfaces/Task";
import { TaskRule } from "@/interfaces/TaskRule";
import { TaskRun } from "@/interfaces/TaskRun";
import {
  Sites,
  Companies,
  Customers,
  TurbineModel,
  Node,
  Turbine,
  Blade,
  BladeComponent,
  BladeDamageType,
  MetricType,
  Metric,
  BladeResponseRecord,
  BladeHealthRecord,
  SystemAggregators,
  BladeModels,
  NewTurbineForm,
  NewCustomerForm,
  NewAggregatorForm,
  NewTurbineModelForm,
} from "@/interfaces/systemsdb";

import {
  Threshold,
  LocalThreshold,
  AlertHistoryResponse,
  toApiThreshold,
  toLocalThreshold,
} from "@/interfaces/Threshold";
import { BalenaDevice } from "@/interfaces/Balena";
import { Temporal } from "@js-temporal/polyfill";
import { ServiceConfiguration } from "@/interfaces/ServiceConfiguration";
import { GrafanaPreferences } from "@/interfaces/GrafanaPreferences";
import { getAuthToken, getBalenaAuthToken } from "./store";
import { InvalidCredentialsError, PermissionsError, TimeoutError } from "@/errors/errors";
import prettifyJSON from "@/components/PrettifyJSON.vue";

// Error handling helper function which checks whether the response is json before trying to read it as json. Avoids
// broken seeming error messages when the server 500s.
async function handleResponseError(response: Response) {
  const contentType = response.headers.get("content-type");
  let message = `HTTP ${response.status}`;
  if (contentType?.includes("application/json")) {
    const err = await response.json();
    message = err.message ?? err.error ?? JSON.stringify(err);
  } else {
    message = await response.text();
  }
  throw new Error(message);
}

const fetchWithToken = async (location: string, init: RequestInit = { headers: {} }): Promise<Response> => {
  const initWithAuth = {
    ...init,
    headers: { ...init.headers, Authorization: `Bearer ${getAuthToken()}` },
  };
  const response = await fetch(location, initWithAuth);
  if (response.status === 401) {
    // Redirect to login page - it'd be nice to tell them at some point
    if (window.location.pathname != "/login") {
      window.location.assign("/login");
    }
  } else if (response.status === 403) {
    //Tell them they don't have permission and stop operation
    throw new PermissionsError("You do not have permission to perform that operation");
  }
  return response;
};

const fetchWithBalenaToken = async (location: string, init: RequestInit = { headers: {} }): Promise<Response> => {
  const appToken = getAuthToken();
  const balenaToken = getBalenaAuthToken(); // from auth/balena/login
  const headers = { ...(init.headers ?? {}) } as Record<string, string>;

  // Keep your normal API auth (so your backend authorizes the call)
  if (appToken) {
    headers["Authorization"] = `Bearer ${appToken}`;
  }

  // Send Balena token in a separate header for the backend to use
  if (balenaToken) {
    headers["X-Balena-Authorization"] = `Bearer ${balenaToken}`;
  }

  const response = await fetch(location, { ...init, headers });

  // If your backend uses 401 to indicate "balena token missing/expired", propagate that upward
  if (response.status === 403) {
    throw new PermissionsError("You do not have permission to perform that operation");
  }

  return response;
};

const fetchOrigins = async (): Promise<EventOrigin[]> => {
  const origins = await fetchWithToken("/api/v1/event_origins");
  return origins.json();
};

const fetchEvents = async (params: URLSearchParams): Promise<Event[]> => {
  const incidents = await fetchWithToken(`/api/v1/events?${params.toString()}`);
  return incidents.json();
};

const fetchIncidents = async (params: URLSearchParams): Promise<Incident[]> => {
  const incidents = await fetchWithToken(`/api/v1/incidents?${params.toString()}`);
  return incidents.json();
};

const fetchSensors = async (): Promise<Sensor[]> => {
  const sensors = await fetchWithToken("/api/v1/sensors");
  return sensors.json();
};

const fetchData = async (start_time: Temporal.Instant): Promise<Data[]> => {
  const data = await fetchWithToken(`/api/v1/data/data?Limit=1000&StartTime=${start_time.epochSeconds}`);
  return data.json();
  // NB: at the moment the API will return the most recent 1000 bursts and the earliest 1000 data samples in the specified period!
};

const fetchBursts = async (params: URLSearchParams): Promise<Burst[]> => {
  const bursts = await fetchWithToken(`/api/v1/data/bursts?${params.toString()}`);
  return bursts.json();
};

const fetchSimilarPeriods = async (params: URLSearchParams): Promise<ConditionMatchedPeriod[]> => {
  const withSortedArrays = new URLSearchParams(params);
  withSortedArrays.set("sorted_arrays", "true");
  const periods = await fetchWithToken(`/api/v1/data/get_similar_periods?${withSortedArrays.toString()}`);
  return periods.json();
};

const fetchAggregators = async (): Promise<Aggregator[]> => {
  const aggregators = await fetchWithToken("/api/v1/aggregators");
  return aggregators.json();
};

const fetchBurst = async (burstId: string): Promise<SingleBurst> => {
  const burst = await fetchWithToken(`/api/v1/data/burst/${burstId}`);
  return burst.json();
};

// The available-task list only changes when a worker registers a new task type (a deploy), so it's
// effectively static within a session. Cache the in-flight promise so repeated callers (e.g. reopening
// the task-rule modal) reuse one request instead of re-hitting the API each time. Cleared on full reload;
// pass forceRefresh to bypass. Failures aren't cached so a later call can retry.
let tasksCache: Promise<Task[]> | null = null;

const fetchTasks = (forceRefresh = false): Promise<Task[]> => {
  if (!tasksCache || forceRefresh) {
    tasksCache = fetchWithToken("/api/v1/tasks")
      .then((tasks) => tasks.json())
      .catch((err) => {
        tasksCache = null;
        throw err;
      });
  }
  return tasksCache;
};

const fetchTaskRules = async (): Promise<TaskRule[]> => {
  const taskRule = await fetchWithToken("/api/v1/task_rules");
  return taskRule.json();
};

const fetchTaskRuns = async (params: URLSearchParams): Promise<TaskRun[]> => {
  const taskRuns = await fetchWithToken(`/api/v1/task_runs?${params.toString()}`);
  return taskRuns.json();
};

// The task-run detail endpoint can hang if the API's connection pool is saturated or its event loop is
// stalled, and without a timeout the logs modal spins forever. Abort after a bounded wait so the caller
// always settles and can surface an error instead.
const TASK_RUN_DETAIL_TIMEOUT_MS = 20000;

const fetchTaskRun = async (taskRunId: number): Promise<TaskRun> => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), TASK_RUN_DETAIL_TIMEOUT_MS);
  try {
    const taskRun = await fetchWithToken(`/api/v1/task_runs/${taskRunId}`, { signal: controller.signal });
    return taskRun.json();
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw new TimeoutError("Timed out loading logs");
    }
    throw error;
  } finally {
    clearTimeout(timeout);
  }
};

const fetchTaskRunCounts = async (params: URLSearchParams): Promise<Record<number, number>> => {
  const taskRuns = await fetchWithToken(`/api/v1/count_task_runs_by_rule?${params.toString()}`);
  return taskRuns.json();
};

const fetchThresholds = async (): Promise<Threshold[]> => {
  const thresholds = await fetchWithToken(`/api/v1/thresholds`);
  return thresholds.json();
};

const fetchAlertHistory = async (thresholdId: number, from?: number, to?: number): Promise<AlertHistoryResponse> => {
  const params = new URLSearchParams();
  if (from !== undefined) params.set("from", from.toString());
  if (to !== undefined) params.set("to", to.toString());
  const query = params.toString() ? `?${params.toString()}` : "";
  const response = await fetchWithToken(`/api/v1/thresholds/${thresholdId}/alert-history${query}`);
  if (!response.ok) {
    await handleResponseError(response);
  }
  return response.json();
};

const fetchSensorTagOptions = async (): Promise<Record<string, string[]>> => {
  const sensorTagOptions = await fetchWithToken(`/api/v1/sensor_tag_options`);
  return sensorTagOptions.json();
};

const fetchSensorNames = async (params: URLSearchParams): Promise<string[]> => {
  const sensorNames = await fetchWithToken(`/api/v1/sensors/names?${params.toString()}`);
  return sensorNames.json();
};

const fetchBalenaDevices = async (): Promise<BalenaDevice[]> => {
  const res = await fetchWithBalenaToken("/api/v1/balena/devices");
  if (res.status === 401) {
    throw new InvalidCredentialsError("Balena login required");
  }
  return res.json();
};

const fetchBalenaFleets = async (): Promise<string[]> => {
  const res = await fetchWithToken("/auth/balena/fleets");
  return res.json();
};

// Returns the TOML file (not base64 encoded)
const fetchBalenaAggregatorConfig = async (deviceUUID: string): Promise<string> => {
  const res = await fetchWithBalenaToken(`/api/v1/balena/device/${deviceUUID}/config.toml`);
  if (res.status === 401) {
    throw new InvalidCredentialsError("Balena login required");
  }
  return res.text();
};

// POSTs config file, currently in TOML format.  Do not base-64 encode it.
const updateBalenaAggregatorConfig = async (deviceUUID: string, conf: string): Promise<void> => {
  const res = await fetchWithBalenaToken(`/api/v1/balena/device/${deviceUUID}/config.toml`, {
    method: "POST",
    body: conf,
    headers: {
      "Content-Type": "application/toml",
    },
  });
  if (res.status === 401) {
    throw new InvalidCredentialsError("Balena login required");
  }
};

const fetchServiceConfiguration = async (): Promise<ServiceConfiguration> => {
  const serviceConfiguration = await fetchWithToken("/api/v1/service_configuration");
  return serviceConfiguration.json();
};

const fetchGrafanaPreferences = async (): Promise<GrafanaPreferences> => {
  const grafanaPreferences = await fetch("/grafana/api/user/preferences");
  return grafanaPreferences.json();
};

const postRerunTask = async (taskID: number): Promise<void> => {
  await fetchWithToken(`/api/v1/rerun_task/${taskID}`, {
    method: "POST",
  });
};

const postNewTask = async (rule: TaskRule): Promise<Response> => {
  const response = await fetchWithToken("/api/v1/new_task_rule", {
    method: "POST",
    body: JSON.stringify(rule),
    headers: {
      "Content-Type": "application/json",
    },
  });
  if (!response.ok) {
    await handleResponseError(response);
  }
  return response.json();
};

const postUpdateTask = async (rule: TaskRule, taskRuleID: number): Promise<Response> => {
  const response = await fetchWithToken(`/api/v1/update_task_rule/${taskRuleID}`, {
    method: "POST",
    body: JSON.stringify(rule),
    headers: {
      "Content-Type": "application/json",
    },
  });
  return response.json();
};

const postNewThreshold = async (threshold: LocalThreshold): Promise<LocalThreshold> => {
  const response = await fetchWithToken("/api/v1/thresholds", {
    method: "POST",
    body: JSON.stringify(toApiThreshold(threshold)),
    headers: {
      "Content-Type": "application/json",
    },
  });
  if (!response.ok) {
    await handleResponseError(response);
  }
  return toLocalThreshold(await response.json());
};

const putUpdateThreshold = async (threshold: LocalThreshold): Promise<LocalThreshold> => {
  const response = await fetchWithToken(`/api/v1/thresholds/${threshold.ThresholdID}`, {
    method: "PUT",
    body: JSON.stringify(toApiThreshold(threshold)),
    headers: {
      "Content-Type": "application/json",
    },
  });
  if (!response.ok) {
    await handleResponseError(response);
  }
  return toLocalThreshold(await response.json());
};

const deleteThreshold = async (thresholdId: number): Promise<Response> => {
  const response = await fetchWithToken(`/api/v1/thresholds/${thresholdId}`, {
    method: "DELETE",
  });
  if (!response.ok) {
    await handleResponseError(response);
  }
  return response;
};

const putUpdateGrafanaPreferences = async (preferences: GrafanaPreferences): Promise<Response> => {
  const response = await fetch("/grafana/api/user/preferences", {
    method: "PUT",
    body: JSON.stringify(preferences),
    headers: {
      "Content-Type": "application/json",
    },
  });
  return response.json();
};

interface AuthResponse extends Response {
  Message: string;
  AuthToken: string;
}

/*
The following are for the systems db
*/

const fetchNodes = async (): Promise<Node[]> => {
  const res = await fetchWithToken("/api/v1/data/nodes?limit=1000");
  return res.json();
};

const patchNode = async (idHex: string, aggregatorName: string | null, bladeName: string | null): Promise<void> => {
  await fetchWithToken(`/api/v1/data/nodes/${idHex}/assign`, {
    method: "PATCH",
    body: JSON.stringify({ aggregator_name: aggregatorName, blade_name: bladeName }),
    headers: { "Content-Type": "application/json" },
  });
};

const fetchSites = async (country?: string, hasTurbines?: boolean, limit?: number): Promise<Sites[]> => {
  const params = new URLSearchParams();

  if (country) {
    params.set("country", country);
  }

  if (hasTurbines) {
    params.set("has_turbines", "true");
  }

  if (limit !== undefined) {
    params.set("Limit", String(limit));
  }

  const query = params.toString() ? `?${params.toString()}` : "";
  const res = await fetchWithToken(`/api/v1/data/list_sites${query}`);
  const data = await res.json();

  return data;
};

const fetchManufacturingCompanies = async (): Promise<Companies[]> => {
  const res = await fetchWithToken("/api/v1/systems/list_manufacturing_companies");
  return res.json();
};

const fetchCountries = async (): Promise<string[]> => {
  const res = await fetchWithToken("/api/v1/systems/list_countries");
  return res.json();
};

const fetchSystemAggregators = async (): Promise<SystemAggregators[]> => {
  const res = await fetchWithToken(`/api/v1/systems/list_system_aggregators`);
  return res.json();
};

const fetchCustomersNames = async (): Promise<string[]> => {
  const res = await fetchWithToken(`/api/v1/systems/list_customers_names`);
  return res.json();
};

const fetchCustomers = async (name?: string): Promise<Customers[]> => {
  const params = new URLSearchParams();

  if (name) {
    params.set("name", name);
  }

  const query = params.toString() ? `?${params.toString()}` : "";
  const res = await fetchWithToken(`/api/v1/systems/list_customers${query}`);
  return res.json();
};

const fetchTurbineModel = async (designer?: string): Promise<TurbineModel[]> => {
  const params = new URLSearchParams();

  if (designer) {
    params.set("designer", designer);
  }

  const query = params.toString() ? `?${params.toString()}` : "";
  const res = await fetchWithToken(`/api/v1/systems/list_turbine_models${query}`);
  return res.json();
};

const fetchMetricTypes = async (): Promise<MetricType[]> => {
  const res = await fetchWithToken("/api/v1/systems/list_metric_types");
  return res.json();
};

const fetchMetrics = async (metricTypeId?: number): Promise<Metric[]> => {
  const params = new URLSearchParams();
  if (metricTypeId !== undefined) params.set("metric_type_id", String(metricTypeId));
  const query = params.toString() ? `?${params.toString()}` : "";
  const res = await fetchWithToken(`/api/v1/systems/list_metrics${query}`);
  return res.json();
};

const postBladeResponse = async (form: Record<string, unknown>): Promise<void> => {
  const response = await fetchWithToken("/api/v1/data/blade_response/new", {
    method: "POST",
    body: JSON.stringify({ form }),
    headers: { "Content-Type": "application/json" },
  });
  if (!response.ok) {
    await handleResponseError(response);
  }
};

const fetchBladeResponse = async (params: {
  site_id?: number | null;
  turbine_id?: number | null;
  blade_id?: number | null;
}): Promise<BladeResponseRecord[]> => {
  const q = new URLSearchParams();
  if (params.blade_id) q.set("blade_id", String(params.blade_id));
  else if (params.turbine_id) q.set("turbine_id", String(params.turbine_id));
  else if (params.site_id) q.set("site_id", String(params.site_id));
  const res = await fetchWithToken(`/api/v1/systems/list_blade_response?${q.toString()}`);
  return res.json();
};

const fetchBladeHealth = async (params: {
  site_id?: number | null;
  turbine_id?: number | null;
  blade_id?: number | null;
  component_id?: number | null;
}): Promise<BladeHealthRecord[]> => {
  const q = new URLSearchParams();
  if (params.blade_id) q.set("blade_id", String(params.blade_id));
  else if (params.turbine_id) q.set("turbine_id", String(params.turbine_id));
  else if (params.site_id) q.set("site_id", String(params.site_id));
  if (params.component_id) q.set("component_id", String(params.component_id));
  const res = await fetchWithToken(`/api/v1/systems/list_blade_health?${q.toString()}`);
  return res.json();
};

const postBladeDamage = async (form: Record<string, unknown>): Promise<void> => {
  const response = await fetchWithToken("/api/v1/data/blade_damage/new", {
    method: "POST",
    body: JSON.stringify({ form }),
    headers: { "Content-Type": "application/json" },
  });
  if (!response.ok) {
    await handleResponseError(response);
  }
};

const fetchBladeComponents = async (): Promise<BladeComponent[]> => {
  const res = await fetchWithToken("/api/v1/systems/list_blade_components");
  return res.json();
};

const fetchBladeDamageTypes = async (componentId: number): Promise<BladeDamageType[]> => {
  const params = new URLSearchParams();
  params.set("component_id", String(componentId));
  const res = await fetchWithToken(`/api/v1/systems/list_blade_damage_types?${params.toString()}`);
  return res.json();
};

const fetchBlades = async (turbineId: number): Promise<Blade[]> => {
  const params = new URLSearchParams();
  params.set("turbine_id", String(turbineId));
  const res = await fetchWithToken(`/api/v1/systems/list_blades?${params.toString()}`);
  return res.json();
};

const fetchTurbines = async (siteId?: number): Promise<Turbine[]> => {
  const params = new URLSearchParams();

  if (siteId !== undefined) {
    params.set("site_id", String(siteId));
  }

  const query = params.toString() ? `?${params.toString()}` : "";
  const res = await fetchWithToken(`/api/v1/systems/list_turbines${query}`);
  return res.json();
};

const fetchBladeManufacturersForGivenTurbine = async (turbineModel: string): Promise<string[]> => {
  const params = new URLSearchParams();
  params.set("turbine_model", turbineModel);

  const res = await fetchWithToken(`/api/v1/systems/list_blade_manufacturers_for_given_turbine?${params.toString()}`);

  return res.json();
};

const fetchBladeModelsForGivenTurbine = async (turbineModel: string, manufacturer: string): Promise<BladeModels[]> => {
  const params = new URLSearchParams();
  params.set("turbine_model", turbineModel);
  params.set("manufacturer", manufacturer);

  const res = await fetchWithToken(`/api/v1/systems/list_blade_models_for_given_turbine?${params.toString()}`);

  return res.json();
};

const postNewTurbine = async (form: NewTurbineForm): Promise<{ status: string; blade_ids: Record<string, number> }> => {
  console.log("postNewTurbine request body:", {
    form,
  });

  const response = await fetchWithToken("/api/v1/data/turbines/new", {
    method: "POST",
    body: JSON.stringify({
      form,
    }),
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    await handleResponseError(response);
  }

  return response.json();
};

const postNewCustomer = async (form: NewCustomerForm): Promise<Response> => {
  console.log("postNewCustomer request body:", {
    form,
  });

  const response = await fetchWithToken("/api/v1/data/customer/new", {
    method: "POST",
    body: JSON.stringify({
      form,
    }),
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    await handleResponseError(response);
  }

  return response.json();
};

const postAggregatorSerial = async (form: NewAggregatorForm): Promise<Response> => {
  console.log("PostAggregatorSerial request body:", {
    form,
  });

  const response = await fetchWithToken("/api/v1/data/agg_serial/new", {
    method: "POST",
    body: JSON.stringify({
      form,
    }),
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    await handleResponseError(response);
  }

  return response.json();
};

const postNewTurbineModel = async (form: NewTurbineModelForm): Promise<void> => {
  const response = await fetchWithToken("/api/v1/data/turbine_model/new", {
    method: "POST",
    body: JSON.stringify({ form }),
    headers: { "Content-Type": "application/json" },
  });

  if (!response.ok) {
    await handleResponseError(response);
  }
};

const postNewBladeModel = async (form: NewTurbineModelForm): Promise<Response> => {
  console.log("postNewBladeModel request body:", {
    form,
  });

  const response = await fetchWithToken("/api/v1/data/blade_model/new", {
    method: "POST",
    body: JSON.stringify({
      form,
    }),
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    await handleResponseError(response);
  }

  return response.json();
};

interface MeResponse {
  Username: string;
  Email: string;
  Roles: string[];
}

const fetchMe = async (): Promise<MeResponse> => {
  const response = await fetchWithToken("/auth/me");
  return response.json();
};

const postLogin = async (username: string, password: string): Promise<AuthResponse> => {
  const response = await fetch("/auth/login", {
    method: "POST",
    body: JSON.stringify({
      Username: username,
      Password: password,
    }),
    headers: {
      "Content-Type": "application/json",
    },
  });
  if (response.status === 401) {
    throw new InvalidCredentialsError("Invalid Credentials");
  }
  return response.json();
};

const postBalenaLogin = async (token: string, fleet: string): Promise<AuthResponse> => {
  const response = await fetch("/auth/balena/login", {
    method: "POST",
    body: JSON.stringify({
      Token: token,
      Fleet: fleet,
    }),
    headers: {
      "Content-Type": "application/json",
    },
  });
  if (response.status === 401) {
    throw new InvalidCredentialsError("Invalid Credentials");
  }
  return response.json();
};

export {
  fetchAlertHistory,
  fetchEvents,
  fetchOrigins,
  fetchIncidents,
  fetchSensors,
  fetchSensorTagOptions,
  fetchSensorNames,
  fetchData,
  fetchBursts,
  fetchSimilarPeriods,
  fetchAggregators,
  fetchBurst,
  fetchTasks,
  fetchTaskRules,
  fetchTaskRuns,
  fetchTaskRun,
  fetchTaskRunCounts,
  fetchThresholds,
  fetchBalenaDevices,
  fetchBalenaAggregatorConfig,
  updateBalenaAggregatorConfig,
  fetchServiceConfiguration,
  fetchGrafanaPreferences,
  postRerunTask,
  postNewTask,
  postUpdateTask,
  postNewThreshold,
  putUpdateThreshold,
  deleteThreshold,
  putUpdateGrafanaPreferences,
  postLogin,
  fetchBalenaFleets,
  postBalenaLogin,
  fetchNodes,
  fetchSites,
  fetchTurbines,
  fetchBlades,
  fetchBladeComponents,
  fetchBladeDamageTypes,
  postBladeDamage,
  fetchBladeHealth,
  fetchBladeResponse,
  fetchMetricTypes,
  fetchMetrics,
  postBladeResponse,
  fetchManufacturingCompanies,
  fetchTurbineModel,
  fetchBladeManufacturersForGivenTurbine,
  fetchBladeModelsForGivenTurbine,
  fetchCountries,
  fetchSystemAggregators,
  fetchCustomers,
  fetchCustomersNames,
  patchNode,
  postNewTurbine,
  postNewCustomer,
  postAggregatorSerial,
  postNewTurbineModel,
  postNewBladeModel,
  fetchMe,
};
